import { useEffect, useState, useMemo } from 'react'
import { Plus, User } from 'lucide-react'
import { Api } from '../../lib/api.js'
import { useAuth } from '../../contexts/AuthContext.jsx'
import { env } from '../../config/env.js'
import ProfileBar from '../review/ProfileBar.jsx'
import Button from '../ui/Button.jsx'

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

export default function ClientStep({ value, onChange }) {
  const { token, onUnauthorized } = useAuth()
  const [allClients, setAllClients] = useState(null)
  const [query, setQuery] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    email: '', phone: '', notes: '',
    profile: { civilStatus: 'marie', hasChildren: true, employment: 'salarie', ownership: 'proprietaire' },
  })
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    api.listClients().then(setAllClients).catch((e) => setErr(e.message))
  }, [token, onUnauthorized])

  const matches = useMemo(() => {
    if (!allClients || !query.trim()) return []
    const q = norm(query)
    return allClients.filter((c) => norm(c.Name || c.name).includes(q)).slice(0, 8)
  }, [allClients, query])

  const exactMatch = matches.find((c) => norm(c.Name || c.name) === norm(query))

  const pickExisting = (c) => {
    onChange({ ...value, client_id: c.ID || c.id, client_name: c.Name || c.name })
  }

  const onCreate = async () => {
    setErr(null)
    if (!query.trim() || !createForm.email.trim()) {
      setErr('Nom et email requis')
      return
    }
    setSubmitting(true)
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => token, onUnauthorized })
    try {
      const [created, preseed] = await Promise.all([
        api.createClient({
          name: query.trim(),
          email: createForm.email.trim(),
          phone: createForm.phone.trim(),
          civil_status: createForm.profile.civilStatus,
          has_children: createForm.profile.hasChildren,
          employment: createForm.profile.employment,
          ownership: createForm.profile.ownership,
        }),
        createForm.notes.trim()
          ? api.preseedFromText(createForm.notes.trim())
          : Promise.resolve({ extracted_facts: null }),
      ])
      onChange({
        ...value,
        client_id: created.ID || created.id,
        client_name: created.Name || created.name,
        pre_seed_facts: preseed.extracted_facts,
      })
    } catch (e) {
      setErr(e.message || 'Erreur')
    } finally {
      setSubmitting(false)
    }
  }

  if (value.client_id) {
    return (
      <div className="rounded-xl border border-line bg-white p-4">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-bg p-2"><User size={14} /></span>
          <div>
            <div className="text-[14px] font-semibold">{value.client_name}</div>
            <div className="text-[11.5px] text-muted">Client sélectionné</div>
          </div>
          <button
            onClick={() => onChange({ ...value, client_id: '', client_name: '', pre_seed_facts: null })}
            className="ml-auto text-[12px] text-muted underline"
          >
            Changer
          </button>
        </div>
      </div>
    )
  }

  const isDecouverte = value.kind === 'decouverte'

  return (
    <div className="max-w-[680px] space-y-3">
      <div className={`rounded-lg border px-3 py-2 text-[12px] ${
        isDecouverte
          ? 'border-line bg-bg/60 text-muted'
          : 'border-terra-500/40 bg-terra-50 text-terra-600'
      }`}>
        {isDecouverte
          ? 'Optionnel — vous pourrez créer le client ou compléter ses informations après la séance.'
          : 'Un client doit être associé pour démarrer la séance.'}
      </div>
      <input
        type="text"
        autoFocus
        placeholder="Nom du client"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setShowCreate(false) }}
        className="w-full rounded-lg border border-line bg-white px-3 py-2 text-[14px]"
      />

      {err && !showCreate && (
        <div className="rounded-lg border border-terra-500/40 bg-terra-50 px-3 py-2 text-[12px] text-terra-600">{err}</div>
      )}

      {matches.length > 0 && (
        <ul className="rounded-lg border border-line bg-white">
          {matches.map((c) => (
            <li key={c.ID || c.id}>
              <button
                onClick={() => pickExisting(c)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-bg"
              >
                <span className="text-[14px]">{c.Name || c.name}</span>
                <span className="text-[11.5px] text-muted">{c.Email || c.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {query.trim() && !exactMatch && !showCreate && (
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg border border-dashed border-line bg-white px-4 py-2.5 text-[13px] text-muted hover:border-terra-500"
        >
          <Plus size={14} /> Créer « {query.trim()} »
        </button>
      )}

      {showCreate && (
        <div className="space-y-3 rounded-xl border border-line bg-white p-4">
          <input
            type="email"
            placeholder="Email"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-[13.5px]"
          />
          <input
            type="tel"
            placeholder="Téléphone (optionnel)"
            value={createForm.phone}
            onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-[13.5px]"
          />
          <ProfileBar
            profile={createForm.profile}
            onChange={(p) => setCreateForm({ ...createForm, profile: p })}
          />
          <textarea
            placeholder="Ce que vous savez déjà sur ce client (optionnel)"
            rows={4}
            value={createForm.notes}
            onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-[13.5px]"
          />
          {err && (
            <div className="rounded-lg border border-terra-500/40 bg-terra-50 px-3 py-2 text-[12px] text-terra-600">{err}</div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className="px-3 py-2 text-[13px] text-muted">Annuler</button>
            <Button size="md" disabled={submitting} onClick={onCreate}>Créer et continuer</Button>
          </div>
        </div>
      )}
    </div>
  )
}
